import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

const SoftwareHero = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 3,
    hours: 23,
    minutes: 59,
    seconds: 59
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prevTime => {
        const newSeconds = prevTime.seconds - 1;
        const newMinutes = newSeconds < 0 ? prevTime.minutes - 1 : prevTime.minutes;
        const newHours = newMinutes < 0 ? prevTime.hours - 1 : prevTime.hours;
        const newDays = newHours < 0 ? prevTime.days - 1 : prevTime.days;

        return {
          days: newDays,
          hours: newHours < 0 ? 23 : newHours,
          minutes: newMinutes < 0 ? 59 : newMinutes,
          seconds: newSeconds < 0 ? 59 : newSeconds
        };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Split "TRANSFORM" into individual letters for animation
  const transformText = "TRANSFORM";
  const transformLetters = transformText.split('');

  return (
    <HeroContainer id="hero">
      <OverlayGradient />
      <ContentWrapper>
        <HeroHeading>
          <TransformTextContainer>
            {transformLetters.map((letter, index) => (
              <AnimatedLetter key={index} delay={index * 0.1}>
                {letter}
              </AnimatedLetter>
            ))}
          </TransformTextContainer>
          <span> YOUR BUSINESS WITH AI</span>
        </HeroHeading>
        <Subheading>
          <StrongText>CUSTOM AI SOLUTIONS</StrongText> Built By Expert Engineers
        </Subheading>
        <OfferBadge>LIMITED AVAILABILITY</OfferBadge>
        <TimerContainer>
          <TimerLabel>Special Consultation Rate Ends In:</TimerLabel>
          <CountdownWrapper>
            <TimeBlock>
              <TimeNumber>{timeLeft.days}</TimeNumber>
              <TimeLabel>Days</TimeLabel>
            </TimeBlock>
            <TimerSeparator>:</TimerSeparator>
            <TimeBlock>
              <TimeNumber>{timeLeft.hours.toString().padStart(2, '0')}</TimeNumber>
              <TimeLabel>Hours</TimeLabel>
            </TimeBlock>
            <TimerSeparator>:</TimerSeparator>
            <TimeBlock>
              <TimeNumber>{timeLeft.minutes.toString().padStart(2, '0')}</TimeNumber>
              <TimeLabel>Minutes</TimeLabel>
            </TimeBlock>
            <TimerSeparator>:</TimerSeparator>
            <TimeBlock>
              <TimeNumber>{timeLeft.seconds.toString().padStart(2, '0')}</TimeNumber>
              <TimeLabel>Seconds</TimeLabel>
            </TimeBlock>
          </CountdownWrapper>
        </TimerContainer>
        <HeroText>
          Get <em>expert guidance</em> on integrating cutting-edge AI technology into your business or have our senior engineers build your custom AI-powered solution from scratch!
        </HeroText>
        <CTAContainer>
          <MainCTA href="#contact">
            BOOK YOUR CONSULTATION
            <CTAArrow>→</CTAArrow>
          </MainCTA>
          <SubCTA href="#features">View Our Services</SubCTA>
        </CTAContainer>
        <TrustBadges>
          <Badge>
            <CheckIcon>✓</CheckIcon>
            <BadgeText>100% Money Back Guarantee</BadgeText>
          </Badge>
          <Badge>
            <CheckIcon>✓</CheckIcon>
            <BadgeText>US-Based Development</BadgeText>
          </Badge>
          <Badge>
            <CheckIcon>✓</CheckIcon>
            <BadgeText>7+ Years Expert Experience</BadgeText>
          </Badge>
        </TrustBadges>
      </ContentWrapper>
      <ImageContainer>
        <HeroImage src="https://cdn.builder.io/api/v1/image/assets/TEMP/15a52c029352744508bb895d179121206cf6d8c12423bbcad7fda88df41e8f36?placeholderIfAbsent=true&apiKey=af3b926ef0c2405e9a702a855cd6e35e" alt="AI Development Solutions" />
      </ImageContainer>
    </HeroContainer>
  );
};

// New animation for sequential letter jumping
const jumpUp = keyframes`
  0%, 100% {
    transform: translateY(0) rotate(0deg);
    text-shadow: 0 5px 10px rgba(0, 0, 0, 0.2);
  }
  50% {
    transform: translateY(-20px) rotate(10deg);
    text-shadow: 0 20px 30px rgba(0, 0, 0, 0.3);
  }
`;

// Keep all existing animations
const pulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 71, 87, 0.7);
  }
  
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 15px rgba(255, 71, 87, 0);
  }
  
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 71, 87, 0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// New styled component for the TRANSFORM text container
const TransformTextContainer = styled.span`
  display: inline-flex;
  color: #FF4757;
  margin-right: 10px;
`;

// New styled component for animated letters
const AnimatedLetter = styled.span`
  display: inline-block;
  font-weight: 800;
  color: #FF4757;
  text-shadow: 2px 2px 0 #FFF, 
               4px 4px 0 rgba(255, 71, 87, 0.4),
               6px 6px 10px rgba(0, 0, 0, 0.3);
  transform-style: preserve-3d;
  perspective: 500px;
  animation: ${jumpUp} 2s ease-in-out infinite;
  animation-delay: ${props => props.delay}s;
  animation-fill-mode: both;
  transform-origin: bottom center;
`;

const HeroContainer = styled.section`
  position: relative;
  display: flex;
  padding: 140px 80px 100px;
  background: linear-gradient(135deg, #1a95c9, #39b9ef);
  min-height: 85vh;
  overflow: hidden;
  
  @media (max-width: 991px) {
    flex-direction: column;
    padding: 120px 40px 60px;
  }
  
  @media (max-width: 768px) {
    padding: 100px 20px 60px;
  }
`;

const OverlayGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at top right, rgba(100, 200, 255, 0.4) 0%, rgba(41, 128, 185, 0.8) 70%);
  z-index: 1;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 2;
  width: 55%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  animation: ${fadeIn} 0.8s ease-out;
  
  @media (max-width: 991px) {
    width: 100%;
    margin-bottom: 40px;
  }
`;

const HeroHeading = styled.h1`
  font-family: 'Manrope', sans-serif;
  font-size: 4rem;
  font-weight: 800;
  line-height: 1.1;
  color: #ffffff;
  margin-bottom: 20px;
  letter-spacing: -0.5px;
  
  @media (max-width: 991px) {
    font-size: 3rem;
  }
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subheading = styled.h2`
  font-family: 'Manrope', sans-serif;
  font-size: 1.8rem;
  font-weight: 600;
  color: #E0FBFC;
  margin-bottom: 30px;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const StrongText = styled.span`
  color: #FFD700;
  font-weight: 800;
`;

const OfferBadge = styled.div`
  display: inline-block;
  background-color: #FF4757;
  color: white;
  font-weight: 700;
  padding: 8px 20px;
  border-radius: 30px;
  font-size: 1rem;
  margin-bottom: 25px;
  animation: ${pulse} 2s infinite;
`;

const TimerContainer = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 30px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const TimerLabel = styled.p`
  color: white;
  font-size: 1rem;
  margin-bottom: 15px;
  text-align: center;
  font-weight: 600;
`;

const CountdownWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TimeBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 5px;
`;

const TimeNumber = styled.div`
  background: linear-gradient(135deg, #024A5F, #0871A4);
  color: white;
  font-size: 2rem;
  font-weight: 700;
  padding: 10px;
  border-radius: 8px;
  min-width: 60px;
  text-align: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    min-width: 50px;
    padding: 8px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
    min-width: 40px;
    padding: 6px;
  }
`;

const TimeLabel = styled.div`
  color: #E0FBFC;
  font-size: 0.8rem;
  margin-top: 5px;
`;

const TimerSeparator = styled.div`
  color: white;
  font-size: 2rem;
  font-weight: 700;
  margin: 0 2px;
`;

const HeroText = styled.p`
  color: #E0FBFC;
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 30px;
  max-width: 80%;
  
  em {
    font-style: italic;
    color: #FFD700;
  }
  
  @media (max-width: 991px) {
    max-width: 100%;
  }
`;

const CTAContainer = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const MainCTA = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(90deg, #FF4757, #FF6B81);
  color: white;
  font-weight: 700;
  font-size: 1.2rem;
  padding: 18px 40px;
  border-radius: 8px;
  text-decoration: none;
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(255, 71, 87, 0.4);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(255, 71, 87, 0.6);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: 0.5s;
  }
  
  &:hover:before {
    left: 100%;
  }
`;

const CTAArrow = styled.span`
  margin-left: 10px;
  font-size: 1.4rem;
  transition: transform 0.3s ease;
  
  ${MainCTA}:hover & {
    transform: translateX(5px);
  }
`;

const SubCTA = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
  padding: 16px 30px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

const TrustBadges = styled.div`
  display: flex;
  gap: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const Badge = styled.div`
  display: flex;
  align-items: center;
`;

const CheckIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #00b894;
  color: white;
  font-size: 0.9rem;
  margin-right: 10px;
`;

const BadgeText = styled.span`
  color: #E0FBFC;
  font-size: 0.9rem;
`;

const ImageContainer = styled.div`
  position: relative;
  z-index: 2;
  width: 45%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 1s ease-out;
  
  @media (max-width: 991px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const HeroImage = styled.img`
  max-width: 100%;
  border-radius: 10px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transform: perspective(1000px) rotateY(-5deg);
  transition: all 0.5s ease;
  
  &:hover {
    transform: perspective(1000px) rotateY(0);
  }
`;

export default SoftwareHero; 